import { useEffect, useRef, useState } from "react";
import { ArrowLeft, ArrowRight, Check, Globe2, LoaderCircle } from "lucide-react";
import { useRouter } from "@shared/client/navigation";
import { signOut, updateProfile } from "@shared/lib/auth-client";
import { joinName } from "@shared/lib/auth-shared";
import { parseSiteUrl, siteLabel } from "@shared/lib/url";
import { Alert, AlertDescription } from "@shared/components/ui/alert";
import { Button } from "@shared/components/ui/button";
import { Input } from "@shared/components/ui/input";
import { Label } from "@shared/components/ui/label";
import { ToggleGroup, ToggleGroupItem } from "@shared/components/ui/toggle-group";
import { cn } from "@shared/lib/utils";
import { useT } from "@shared/client/locale";
import { MESSAGES, type Messages } from "@shared/i18n";

/**
 * L'onboarding en trois étapes : vous, votre site, la source. Une question
 * par écran, le fil des étapes nommées en haut (on voit que c'est court), et
 * à droite ce que l'étape engage : ce que fera l'agent, puis le récapitulatif
 * avant de lancer la première analyse.
 */

type Step = 1 | 2 | 3;
const STEPS: Array<{ step: Step; key: keyof Messages["auth"]["onboarding"]["steps"] }> = [
  { step: 1, key: "you" },
  { step: 2, key: "site" },
  { step: 3, key: "last" },
];

type Role = keyof Messages["auth"]["onboarding"]["roles"];
const ROLES: Role[] = ["founder", "sales", "marketing", "agency"];
const OTHER: Role = "other";

type Source = keyof Messages["auth"]["onboarding"]["sources"];
const ACQUISITION_SOURCES: Source[] = ["google", "linkedin", "referral", "email", "content", "event", "ad", "other"];

/**
 * A role saved earlier, as the key of a suggested role when it is one of
 * their labels in any language; else "" (no role) or `other` (typed text).
 */
function roleKey(saved: string): Role | "" {
  if (!saved) return "";
  const match = ROLES.find((role) => Object.values(MESSAGES).some((copy) => copy.auth.onboarding.roles[role] === saved));
  return match ?? OTHER;
}

/** Des boutons en pastille, à choix unique et facultatif (un second clic désélectionne). */
function Chips({ label, options, value, onChange }: {
  label: string;
  options: Array<{ value: string; label: string }>;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <ToggleGroup aria-label={label} className="flex-wrap gap-2" onValueChange={onChange} type="single" value={value}>
      {options.map((option) => (
        <ToggleGroupItem
          className="h-9 flex-none rounded-full border border-[#dcdce0] px-3.5 text-[13.5px] font-normal first:rounded-full last:rounded-full hover:bg-muted hover:text-foreground data-[state=on]:border-foreground data-[state=on]:font-medium max-md:h-10"
          key={option.value}
          value={option.value}
        >
          {option.value === value && <Check aria-hidden="true" className="size-3.5" />}
          {option.label}
        </ToggleGroupItem>
      ))}
    </ToggleGroup>
  );
}

/** Le site répond-il ? Indicatif : un échec n'empêche pas de continuer. */
function useSiteCheck(url: string | null) {
  const [result, setResult] = useState<{ url: string; reachable: boolean } | null>(null);
  useEffect(() => {
    if (!url) return;
    const controller = new AbortController();
    const timer = setTimeout(() => {
      fetch(`/api/onboarding/site-check?url=${encodeURIComponent(url)}`, { signal: controller.signal })
        .then((response) => (response.ok ? response.json() : null))
        .then((data: { reachable?: boolean } | null) => setResult({ url, reachable: data?.reachable !== false }))
        .catch(() => {});
    }, 450);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [url]);
  if (!url) return "idle";
  if (result?.url !== url) return "checking";
  return result.reachable ? "ok" : "unreachable";
}

function Field({ id, label, hint, children }: { id: string; label: React.ReactNode; hint?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="grid content-start gap-2">
      <Label className="text-[13px]" htmlFor={id}>{label}</Label>
      {children}
      {hint && <p className="m-0 text-[12.5px] text-muted-foreground">{hint}</p>}
    </div>
  );
}

const inputClass = "h-11 text-[14.5px] max-md:text-[16px]";

export function OnboardingFlow({
  email,
  initialFirstName = "",
  initialLastName = "",
  initialCompany = "",
  initialRole = "",
  initialWebsite = "",
}: {
  email: string;
  initialFirstName?: string;
  initialLastName?: string;
  initialCompany?: string;
  initialRole?: string;
  initialWebsite?: string;
}) {
  const router = useRouter();
  const t = useT();
  const copy = t.auth.onboarding;
  const [step, setStep] = useState<Step>(1);
  const [firstName, setFirstName] = useState(initialFirstName);
  const [lastName, setLastName] = useState(initialLastName);
  const [company, setCompany] = useState(initialCompany);
  const initialRoleKey = roleKey(initialRole);
  const [role, setRole] = useState<string>(initialRoleKey);
  const [otherRole, setOtherRole] = useState(initialRoleKey === OTHER ? initialRole : "");
  const [website, setWebsite] = useState(initialWebsite);
  const [source, setSource] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const headingRef = useRef<HTMLHeadingElement>(null);
  const firstRender = useRef(true);

  const siteUrl = parseSiteUrl(website);
  const siteStatus = useSiteCheck(step === 2 ? siteUrl : null);
  const label = siteUrl ? siteLabel(siteUrl) : "";
  // The role is profile text, saved in the account's language; the source keeps
  // its French label, the value the admin reads.
  const roleValue = role === OTHER ? otherRole.trim() : role ? copy.roles[role as Role] : "";
  const prefilledName = Boolean(initialFirstName || initialLastName);

  // Le titre de l'étape reçoit le focus : un lecteur d'écran annonce où l'on est.
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    headingRef.current?.focus();
  }, [step]);

  function go(next: Step) {
    setError("");
    setStep(next);
    window.scrollTo({ top: 0 });
  }

  async function finish() {
    setBusy(true);
    setError("");
    try {
      await updateProfile({
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        full_name: joinName(firstName, lastName),
        company: company.trim(),
        role: roleValue,
        website: siteUrl ?? website.trim(),
        ...(source ? { acquisition_source: MESSAGES.fr.auth.onboarding.sources[source as Source] } : {}),
        onboarding_completed: true,
        // La visite guidée attend l'utilisateur sur son espace, une seule fois.
        product_tour_pending: true,
      });
      // Provisionne le compte client côté admin, émet onboarding_completed et
      // lance la première analyse sur le site déclaré (profil d'offre + leads).
      await fetch("/api/onboarding/complete", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ website: siteUrl ?? website.trim() }),
      }).catch(() => {});
      router.replace("/app");
      router.refresh();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : copy.errors.saveFailed);
      setBusy(false);
    }
  }

  function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (step === 1) go(2);
    else if (step === 2) {
      if (!siteUrl) {
        setError(copy.errors.invalidSite);
        return;
      }
      go(3);
    } else void finish();
  }

  async function switchAccount() {
    await signOut().catch(() => {});
    router.replace("/auth/login");
    router.refresh();
  }

  const titles: Record<Step, { title: string; lede: string }> = {
    1: {
      title: initialFirstName ? copy.titles.welcomeName(initialFirstName) : copy.titles.welcome,
      lede: prefilledName ? copy.titles.welcomeLedePrefilled : copy.titles.welcomeLede,
    },
    2: { title: copy.titles.site, lede: copy.titles.siteLede },
    3: { title: copy.titles.source, lede: copy.titles.sourceLede },
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="flex h-16 items-center gap-6 border-b px-8 max-md:h-14 max-md:px-4">
        <span className="inline-flex items-center gap-2.5 text-[15px] font-semibold tracking-tight">
          <img alt="" className="size-7 object-contain" height={28} src="/brand/Logo.svg" width={28} />
          Hune
        </span>
        <ol aria-label={copy.steps.label} className="m-0 mx-auto flex list-none items-center gap-1.5 p-0 max-md:hidden">
          {STEPS.map(({ step: index, key }, position) => {
            const name = copy.steps[key];
            const done = index < step;
            const content = (
              <>
                <span
                  className={cn(
                    "grid size-5 place-items-center rounded-full border text-[11.5px] font-medium",
                    index === step && "border-foreground",
                    done && "border-foreground bg-foreground text-background",
                  )}
                >
                  {done ? <Check aria-hidden="true" className="size-3" strokeWidth={3} /> : index}
                </span>
                {name}
              </>
            );
            const itemClass = cn(
              "flex items-center gap-2 rounded-md px-2.5 py-1.5 text-[13px] text-muted-foreground",
              index === step && "bg-accent text-foreground",
            );
            return (
              <li className="flex items-center gap-1.5" key={index}>
                {position > 0 && <span aria-hidden="true" className="h-px w-[18px] bg-border" />}
                {done ? (
                  <button className={cn(itemClass, "cursor-pointer border-0 bg-transparent hover:bg-muted hover:text-foreground")} onClick={() => go(index)} type="button">
                    {content}
                  </button>
                ) : (
                  <span aria-current={index === step ? "step" : undefined} className={itemClass}>{content}</span>
                )}
              </li>
            );
          })}
        </ol>
        <span className="flex items-center gap-2 text-[12.5px] text-muted-foreground max-md:hidden">
          {email}
          <button className="cursor-pointer border-0 bg-transparent p-0 text-muted-foreground underline underline-offset-2 hover:text-foreground" onClick={switchAccount} type="button">
            {copy.switchAccount}
          </button>
        </span>
        <span aria-hidden="true" className="ml-auto hidden items-center gap-2 text-[12.5px] text-muted-foreground tabular-nums max-md:flex">
          {copy.stepCounter(step, STEPS.length)}
          <span className="flex gap-[3px]">
            {STEPS.map(({ step: index }) => (
              <i className={cn("h-[3px] w-[18px] rounded-sm", index <= step ? "bg-foreground" : "bg-border")} key={index} />
            ))}
          </span>
        </span>
      </header>

      {/* La première étape n'a pas de colonne d'appui : le formulaire se centre. */}
      <div
        className={cn(
          "mx-auto grid max-w-[1072px] justify-center gap-18 px-8 py-16 max-md:px-4 max-md:pt-7 max-md:pb-32",
          step === 1 ? "lg:grid-cols-[minmax(0,560px)]" : "lg:grid-cols-[minmax(0,560px)_minmax(0,440px)]",
        )}
      >
        <form className="min-w-0" noValidate={step !== 1} onSubmit={submit}>
          <h1
            className="mt-0 mb-2.5 font-serif text-[38px] leading-[1.1] font-normal tracking-[-0.03em] outline-none max-md:text-[30px]"
            ref={headingRef}
            tabIndex={-1}
          >
            {titles[step].title}
          </h1>
          <p className="mt-0 mb-8 text-[15px] leading-relaxed text-muted-foreground">{titles[step].lede}</p>

          {step === 1 && (
            <div className="grid gap-5.5">
              <div className="grid gap-2">
                <div className="grid grid-cols-2 gap-4">
                  <Field id="onboarding-first-name" label={copy.fields.firstName}>
                    <Input autoComplete="given-name" autoFocus={!firstName} className={inputClass} id="onboarding-first-name" onChange={(event) => setFirstName(event.target.value)} required value={firstName} />
                  </Field>
                  <Field id="onboarding-last-name" label={copy.fields.lastName}>
                    <Input autoComplete="family-name" className={inputClass} id="onboarding-last-name" onChange={(event) => setLastName(event.target.value)} required value={lastName} />
                  </Field>
                </div>
                {prefilledName && <p className="m-0 text-[12.5px] text-muted-foreground">{copy.fields.prefilled}</p>}
              </div>
              <Field id="onboarding-company" label={copy.fields.company}>
                <Input autoComplete="organization" autoFocus={Boolean(firstName) && !company} className={inputClass} id="onboarding-company" onChange={(event) => setCompany(event.target.value)} required value={company} />
              </Field>
              <div className="grid gap-2.5">
                <span className="text-[13px] leading-none font-medium" id="onboarding-role">
                  {copy.fields.role} <span className="font-normal text-muted-foreground">{copy.fields.optional}</span>
                </span>
                <Chips
                  label={copy.fields.role}
                  onChange={setRole}
                  options={[...ROLES, OTHER].map((value) => ({ value, label: copy.roles[value] }))}
                  value={role}
                />
                {role === OTHER && (
                  <Input
                    aria-label={copy.fields.otherRoleLabel}
                    autoFocus
                    className={cn(inputClass, "mt-1")}
                    maxLength={160}
                    onChange={(event) => setOtherRole(event.target.value)}
                    placeholder={copy.fields.otherRolePlaceholder}
                    value={otherRole}
                  />
                )}
              </div>
            </div>
          )}

          {step === 2 && (
            <Field
              hint={siteStatus === "unreachable" ? copy.fields.websiteUnreachable : copy.fields.websiteHint}
              id="onboarding-website"
              label={copy.fields.website}
            >
              <div className="relative">
                <Globe2 aria-hidden="true" className="pointer-events-none absolute top-1/2 left-3.5 size-[18px] -translate-y-1/2 text-muted-foreground" />
                <Input
                  autoComplete="url"
                  autoFocus
                  className="h-[52px] pr-36 pl-11 text-[16px]"
                  id="onboarding-website"
                  inputMode="url"
                  onChange={(event) => setWebsite(event.target.value)}
                  placeholder={copy.fields.websitePlaceholder}
                  value={website}
                />
                <span aria-live="polite" className="absolute top-1/2 right-3.5 flex -translate-y-1/2 items-center gap-1.5 text-[12.5px] text-muted-foreground">
                  {siteStatus === "checking" && <><LoaderCircle aria-hidden="true" className="size-3.5 animate-spin" /> {copy.siteStatus.checking}</>}
                  {siteStatus === "ok" && <><Check aria-hidden="true" className="size-3.5 text-success" /> {copy.siteStatus.ok}</>}
                  {siteStatus === "unreachable" && <><span aria-hidden="true" className="size-1.5 rounded-full bg-muted-foreground" /> {copy.siteStatus.unreachable}</>}
                </span>
              </div>
            </Field>
          )}

          {step === 3 && (
            <Chips
              label={copy.fields.sourceLabel}
              onChange={setSource}
              options={ACQUISITION_SOURCES.map((value) => ({ value, label: copy.sources[value] }))}
              value={source}
            />
          )}

          {error && (
            <Alert className="mt-6" role="alert" variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="mt-9 flex items-center gap-3 max-md:fixed max-md:inset-x-0 max-md:bottom-0 max-md:z-10 max-md:m-0 max-md:border-t max-md:bg-background max-md:px-4 max-md:pt-3 max-md:pb-[calc(1.25rem+env(safe-area-inset-bottom))]">
            {step > 1 && (
              <Button className="h-11 px-2 text-muted-foreground max-md:px-3" onClick={() => go((step - 1) as Step)} type="button" variant="ghost">
                <ArrowLeft /> {copy.actions.back}
              </Button>
            )}
            <Button className="h-11 px-5 text-[14.5px] max-md:flex-1" disabled={busy} type="submit">
              {busy && <LoaderCircle className="animate-spin" />}
              {step === 3 ? (busy ? copy.actions.launching : copy.actions.launch) : copy.actions.continue}
              {!busy && <ArrowRight />}
            </Button>
            <span className="ml-auto text-[12.5px] text-muted-foreground max-md:hidden">
              {copy.actions.or} <kbd className="rounded border bg-muted px-1.5 py-0.5 font-sans text-[11.5px] font-medium">{copy.actions.enterKey}</kbd>
            </span>
          </div>
          {step === 3 && <p className="mt-3 mb-0 text-[13px] text-muted-foreground">{copy.actions.launchAnyway}</p>}
        </form>

        {step === 2 && (
          <aside className="self-start rounded-xl bg-muted p-7 max-lg:hidden">
            <h2 className="m-0 mb-1 text-[14px] font-semibold">{copy.agentPlan.title}</h2>
            <p className="mt-0 mb-5 text-[13px] text-muted-foreground">{copy.agentPlan.timing}</p>
            <ol className="m-0 grid list-none p-0">
              {[
                [label ? copy.agentPlan.readsSite(label) : copy.agentPlan.readsYourSite, copy.agentPlan.readsSiteBody],
                [copy.agentPlan.target, copy.agentPlan.targetBody],
                [copy.agentPlan.signals, copy.agentPlan.signalsBody],
                [copy.agentPlan.leads, copy.agentPlan.leadsBody],
              ].map(([title, body], index, all) => (
                <li className="relative grid grid-cols-[20px_1fr] gap-3 pb-4.5 text-[13.5px] leading-normal last:pb-0" key={title}>
                  <span aria-hidden="true" className="z-[1] mt-[5px] ml-[5px] size-[9px] rounded-full border-[1.5px] border-muted-foreground/60 bg-muted" />
                  {index < all.length - 1 && <span aria-hidden="true" className="absolute top-4 bottom-0 left-[9.5px] w-px bg-border" />}
                  <div>
                    <strong className="font-medium">{title}</strong>
                    <span className="block text-muted-foreground">{body}</span>
                  </div>
                </li>
              ))}
            </ol>
          </aside>
        )}
        {step === 3 && (
          <aside className="self-start rounded-xl bg-muted p-7 max-lg:hidden">
            <h2 className="m-0 mb-1 text-[14px] font-semibold">{copy.summary.title}</h2>
            <p className="mt-0 mb-5 text-[13px] text-muted-foreground">{copy.summary.editable}</p>
            <dl className="m-0 rounded-lg border bg-background">
              {([
                [copy.summary.you, [joinName(firstName, lastName), roleValue].filter(Boolean).join(" · "), 1],
                [copy.summary.company, company.trim(), 1],
                [copy.summary.site, label, 2],
              ] as const).map(([term, value, target]) => (
                <div className="flex items-center gap-4 border-t px-4 py-3 text-[13.5px] first:border-t-0" key={term}>
                  <dt className="w-[92px] shrink-0 text-[13px] text-muted-foreground">{term}</dt>
                  <dd className="m-0 min-w-0 flex-1 truncate font-medium">{value || "—"}</dd>
                  <button className="cursor-pointer border-0 bg-transparent p-0 text-[12.5px] text-muted-foreground underline underline-offset-2 hover:text-foreground" onClick={() => go(target)} type="button">
                    {copy.summary.edit}<span className="sr-only">{copy.summary.editTarget(term)}</span>
                  </button>
                </div>
              ))}
            </dl>
            <p className="mt-3.5 mb-0 text-[13px] leading-relaxed text-muted-foreground">{copy.summary.noContact}</p>
          </aside>
        )}
      </div>
    </div>
  );
}
